/**
 * OperationalKanban: the board family's Kanban layout (docs/design/kanban.md).
 *
 *   columns      the server's, in order, each with its exact count and a page of cards
 *   renderCard   the workspace draws what a card says (phrasing content only)
 *   transitions  the workspace's map of which drops OPEN which governed act
 *   onAct        how the workspace opens an act (UMED: the case panel, pre-targeted)
 *   detail       the docked record (DetailDock), never a dropdown inside a column
 *
 * A drop never writes. It resolves through the transition map (resolveDrop):
 * an act the card may take calls onAct and nothing else; anything else is
 * refused in the board's status line with the reason and, where the map has
 * one, the thing to do instead. The server moves the card when the act it
 * opened is recorded, and the next read shows it.
 *
 * Keyboard: Tab reaches each card's open button, its drag grip and "Move…".
 * On the grip, Space picks the card up, Left / Right choose a column, Space
 * drops, Escape cancels -- and every step is announced. "Move…" lists the
 * same acts without dragging. With a record open: Escape closes it, Alt+Up /
 * Alt+Down step through the column.
 *
 * Phones (<= 640px) show one column at a time, chosen from a switcher with
 * each column's count. Below 900px the open record is a full-screen sheet.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, pointerWithin, rectIntersection, useSensor, useSensors,
} from '@dnd-kit/core';
import type {
  Announcements, CollisionDetection, DragEndEvent, DragOverEvent, DragStartEvent, KeyboardCoordinateGetter, UniqueIdentifier,
} from '@dnd-kit/core';
import DetailDock, { useNarrow } from '../DetailDock';
import useSheetModal from '../useSheetModal';
import KanbanColumn from './KanbanColumn';
import type { DropHint } from './KanbanColumn';
import WorkCard from './WorkCard';
import KanbanToolbar from './KanbanToolbar';
import ElsewhereStrip from './ElsewhereStrip';
import useScrollFade from './useScrollFade';
import { mayTakeFocus } from './focusRule';
import { offeredMoves, resolveDrop } from './transitions';
import type { OfferedMove } from './transitions';
import type { DropResolution, ElsewhereBucket, KanbanCardBase, KanbanColumnData, KanbanDetail, KanbanToolbarProps, TransitionMap } from './types';

export interface OperationalKanbanProps<C extends KanbanCardBase, Ctx> {
  /** The board's accessible name ("UMED board"). */
  label: string;
  /** A stable id prefix for this board's regions. */
  idBase: string;
  columns: Array<KanbanColumnData<C>>;
  renderCard: (card: C) => React.ReactNode;
  /** The record's name for every accessible name ("Ann Nelson"). */
  cardName: (card: C) => string;
  transitions: TransitionMap<C, Ctx>;
  /** What the transition map's `available` checks are given (the viewer's capabilities, usually). */
  ctx: Ctx;
  /** Open the governed act `act` for `card`. Never a write by itself. */
  onAct: (card: C, act: string, move: { from: string; to: string | null }) => void;
  onLoadMore?: (columnKey: string) => void;
  toolbar?: KanbanToolbarProps;
  elsewhere?: { label: string; buckets: ElsewhereBucket[]; note?: React.ReactNode };
  detail?: KanbanDetail<C>;
  loading?: boolean;
  /** A failed read, said in place of the board. */
  error?: string | null;
  onRetry?: () => void;
  /** Text for a column with no cards. */
  emptyText?: string;
  /** Turn drag off (e.g. a read-only viewer). "Move…" still lists the acts on offer. */
  dragDisabled?: boolean;
  /** A notice from the host (after an act), said in the board's status line. */
  notice?: string | null;
  /** What one card is, for each column count's accessible name (default ['record', 'records']). */
  unit?: [string, string];
}

/** The board's own status line: what a drop did, or why it was refused. */
type Said = { text: string; tone: 'info' | 'refuse'; instead?: { label: string; act: string; cardId: string } } | null;

function prefersReducedMotion(): boolean {
  try { return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

/**
 * Left / Right moves the picked-up card to the next column (not 25px at a
 * time); Up / Down are swallowed, because a column's order is the server's.
 */
const columnKeyboardCoordinates: KeyboardCoordinateGetter = (event, { context, currentCoordinates }) => {
  const { droppableRects, droppableContainers, collisionRect, over } = context;
  if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.code)) return undefined;
  event.preventDefault();
  if (event.code === 'ArrowUp' || event.code === 'ArrowDown' || !collisionRect) return currentCoordinates;
  const cols = droppableContainers.getEnabled()
    .map((c) => ({ id: c.id, rect: droppableRects.get(c.id) }))
    .filter((c): c is { id: UniqueIdentifier; rect: NonNullable<typeof c.rect> } => Boolean(c.rect))
    .sort((a, b) => a.rect.left - b.rect.left);
  if (!cols.length) return currentCoordinates;
  const centre = collisionRect.left + collisionRect.width / 2;
  let at = over ? cols.findIndex((c) => c.id === over.id) : -1;
  if (at < 0) at = cols.findIndex((c) => centre >= c.rect.left && centre <= c.rect.left + c.rect.width);
  const next = cols[Math.min(Math.max((at < 0 ? 0 : at) + (event.code === 'ArrowRight' ? 1 : -1), 0), cols.length - 1)];
  return { x: next.rect.left + Math.max(0, (next.rect.width - collisionRect.width) / 2), y: next.rect.top + 72 };
};

/** Under the pointer when there is one; otherwise (the keyboard) whatever the moved card overlaps most. */
const collision: CollisionDetection = (args) => {
  const hit = pointerWithin(args);
  return hit.length ? hit : rectIntersection(args);
};

/** After the drop's own focus restore (one animation frame) and before a workspace's act target is looked for. */
export const DOCK_FOCUS_DELAY_MS = 80;

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

const columnOf = (over: { data?: { current?: { column?: string } | null } } | null | undefined): string | null =>
  (over && over.data && over.data.current && over.data.current.column) || null;

export default function OperationalKanban<C extends KanbanCardBase, Ctx>({
  label, idBase, columns, renderCard, cardName, transitions, ctx, onAct, onLoadMore, toolbar, elsewhere, detail,
  loading = false, error = null, onRetry, emptyText, dragDisabled = false, notice = null, unit,
}: OperationalKanbanProps<C, Ctx>) {
  const phone = useNarrow(640);
  const sheet = useNarrow(899);
  const order = useMemo(() => columns.map((c) => c.key), [columns]);
  const labelOf = useCallback((key: string) => columns.find((c) => c.key === key)?.label || key, [columns]);
  const byId = useMemo(() => {
    const m = new Map<string, C>();
    for (const c of columns) for (const k of c.cards) m.set(k.id, k);
    return m;
  }, [columns]);

  /* ── The status line ───────────────────────────────────────────────── */
  const [said, setSaid] = useState<Said>(null);
  const saidRef = useRef<HTMLParagraphElement | null>(null);
  useEffect(() => { if (notice) setSaid({ text: notice, tone: 'info' }); }, [notice]);

  /*
   * After an act opens, focus goes to the docked record (verification 2026-10-14): a keyboard drop's focus restore
   * (dnd-kit puts it back on the grip a frame later) and a "Move…" item that unmounts both left the person outside
   * the act they just opened. Tried after that frame, for as long as the dock takes to appear; if something inside
   * the dock already holds focus (the act's own control), it is left there.
   */
  const bodyRef = useRef<HTMLDivElement | null>(null);
  /** The lanes' region (the board body without the toolbar): with the record, where the board's own keys apply. */
  const lanesBodyRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const dockFocusTimer = useRef<number | null>(null);
  /** Where focus was when the act started: the board takes focus only while it is still there (focusRule.mayTakeFocus). */
  const actOrigin = useRef<Element | null>(null);
  useEffect(() => () => { if (dockFocusTimer.current !== null) window.clearTimeout(dockFocusTimer.current); }, []);
  const focusDockSoon = useCallback(() => {
    if (dockFocusTimer.current !== null) window.clearTimeout(dockFocusTimer.current);
    let tries = 0;
    const tick = () => {
      dockFocusTimer.current = null;
      tries += 1;
      const dock = bodyRef.current ? bodyRef.current.querySelector<HTMLElement>('.board-dock') : null;
      const active = typeof document !== 'undefined' ? document.activeElement : null;
      if (dock && active && active !== document.body && dock.contains(active)) return;
      // The person has moved on (the search box, another card): nothing takes focus back.
      if (!mayTakeFocus(active, actOrigin.current)) return;
      if (dock && headingRef.current) { headingRef.current.focus(); return; }
      if (tries < 20) dockFocusTimer.current = window.setTimeout(tick, 50);
    };
    dockFocusTimer.current = window.setTimeout(tick, DOCK_FOCUS_DELAY_MS);
  }, []);

  const act = useCallback((card: C, r: DropResolution) => {
    if (r.kind === 'none') return;
    if (r.kind === 'act') {
      setSaid({ text: `${cardName(card)}: ${r.opens}. Nothing is recorded until you confirm it there.`, tone: 'info' });
      actOrigin.current = typeof document !== 'undefined' ? document.activeElement : null;
      onAct(card, r.act, { from: r.from, to: r.to });
      focusDockSoon();
      return;
    }
    setSaid({
      text: `${cardName(card)} was not moved to ${labelOf(r.to)}. ${r.reason}`,
      tone: 'refuse',
      ...(r.instead ? { instead: { ...r.instead, cardId: card.id } } : {}),
    });
  }, [cardName, labelOf, onAct, focusDockSoon]);

  /* ── Drag ──────────────────────────────────────────────────────────── */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: columnKeyboardCoordinates }),
  );
  const [dragging, setDragging] = useState<C | null>(null);
  const [overCol, setOverCol] = useState<string | null>(null);
  const hintFor = useCallback((key: string): { hint: DropHint; text: string | null } => {
    if (!dragging) return { hint: null, text: null };
    if (key === dragging.column) return { hint: 'origin', text: 'Where the record is now' };
    const r = resolveDrop(transitions, dragging, dragging.column, key, ctx);
    if (r.kind === 'act') return { hint: 'act', text: `Drop here: ${lowerFirst(r.opens)}` };
    if (r.kind === 'refuse') return { hint: 'refuse', text: r.reason };
    return { hint: null, text: null };
  }, [dragging, transitions, ctx]);

  const announcements: Announcements = useMemo(() => ({
    onDragStart: ({ active }) => {
      const k = byId.get(String(active.id));
      return k ? `Picked up ${cardName(k)}, in ${labelOf(k.column)}. Left and right arrows choose a column; Space drops; Escape cancels. Dropping opens the act for that move and records nothing.` : '';
    },
    onDragOver: ({ active, over }) => {
      const k = byId.get(String(active.id));
      const to = columnOf(over);
      if (!k || !to) return k ? `${cardName(k)} is not over a column.` : '';
      if (to === k.column) return `${cardName(k)} is over ${labelOf(to)}, where it is now.`;
      const r = resolveDrop(transitions, k, k.column, to, ctx);
      return r.kind === 'act' ? `Over ${labelOf(to)}. Dropping here ${lowerFirst(r.opens)}; nothing is recorded by the drop.`
        : r.kind === 'refuse' ? `Over ${labelOf(to)}: not allowed. ${r.reason}` : '';
    },
    // What a drop did is said ONCE, by the board's status line (polite); this region only says a drop that did nothing.
    onDragEnd: ({ active, over }) => {
      const k = byId.get(String(active.id));
      const to = columnOf(over);
      if (!k) return undefined;
      const r = resolveDrop(transitions, k, k.column, to, ctx);
      return r.kind === 'none' ? `${cardName(k)} stays in ${labelOf(k.column)}.` : undefined;
    },
    onDragCancel: ({ active }) => {
      const k = byId.get(String(active.id));
      return k ? `Cancelled. ${cardName(k)} stays in ${labelOf(k.column)}.` : 'Cancelled.';
    },
  }), [byId, cardName, labelOf, transitions, ctx]);

  const onDragStart = (e: DragStartEvent) => { setDragging(byId.get(String(e.active.id)) || null); setOverCol(null); };
  const onDragOver = (e: DragOverEvent) => setOverCol(columnOf(e.over));
  const onDragEnd = (e: DragEndEvent) => {
    const k = byId.get(String(e.active.id));
    setDragging(null); setOverCol(null);
    if (!k) return;
    act(k, resolveDrop(transitions, k, k.column, columnOf(e.over), ctx));
  };
  const onDragCancel = () => { setDragging(null); setOverCol(null); };

  /* ── The open record ───────────────────────────────────────────────── */
  const openId = detail ? detail.openId : null;
  /** The record a card opens: itself, unless the workspace says otherwise (detail.recordOf, 2026-10-14). */
  const recordOf = useCallback((card: C) => (detail && detail.recordOf ? detail.recordOf(card) : card.id), [detail]);
  /** The card that opened the current record, so closing it returns focus there (not to another card of the same record). */
  const opener = useRef<{ record: string; card: string } | null>(null);
  const openCard = openId ? byId.get(openId) || null : null;
  const openCol = openCard ? columns.find((c) => c.key === openCard.column) : undefined;
  const openIdx = openCard && openCol ? openCol.cards.findIndex((k) => k.id === openCard.id) : -1;
  const openBtns = useRef<Record<string, HTMLButtonElement | null>>({});
  const focusPanel = useRef(false);
  const returnTo = useRef<string | null>(null);
  const covered = Boolean(openId && sheet && detail);
  useSheetModal(covered, () => (bodyRef.current ? bodyRef.current.querySelector<HTMLElement>('.board-dock') : null));

  const open = useCallback((card: C) => {
    if (!detail) return;
    const record = recordOf(card);
    if (detail.openId === record) { returnTo.current = card.id; opener.current = null; detail.onClose(); return; }
    opener.current = { record, card: card.id };
    focusPanel.current = true;
    detail.onOpen(record);
  }, [detail, recordOf]);
  const close = useCallback(() => {
    if (!detail || !detail.openId) return;
    returnTo.current = opener.current && opener.current.record === detail.openId ? opener.current.card : detail.openId;
    opener.current = null;
    detail.onClose();
  }, [detail]);
  const step = useCallback((delta: number) => {
    if (!detail || !openCol || openIdx < 0) return;
    const next = openCol.cards[openIdx + delta];
    if (next) detail.onOpen(next.id);
  }, [detail, openCol, openIdx]);

  useEffect(() => {
    if (openId && focusPanel.current) { focusPanel.current = false; headingRef.current?.focus(); }
    if (!openId && returnTo.current) {
      const k = returnTo.current;
      returnTo.current = null;
      openBtns.current[k]?.focus();
    }
  }, [openId]);

  const keys = useRef({ close, step });
  keys.current = { close, step };
  useEffect(() => {
    if (!openId || typeof document === 'undefined') return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const t = e.target instanceof HTMLElement ? e.target : null;
      // The lanes and the record are the board's own (as before the record moved beside the whole stage, 2026-10-30);
      // the toolbar, the Elsewhere strip and the status line keep their keys.
      const dockEl = bodyRef.current ? bodyRef.current.querySelector('.board-dock') : null;
      const own = !t || t === document.body || Boolean(lanesBodyRef.current && lanesBodyRef.current.contains(t)) || Boolean(dockEl && dockEl.contains(t));
      if (!own) return;
      // A dialog or menu inside the record owns its own Escape; a field keeps its keys. The phone sheet is itself a
      // role=dialog since #407, so its own heading and buttons must still close it (as BoardTable has it; verification
      // of #414, 2026-10-14: at 375px Escape inside the Kanban's record sheet did nothing).
      const owner = t ? t.closest('[role="dialog"], dialog, [role="menu"]') : null;
      if (t && ((owner && !owner.classList.contains('board-dock')) || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
      if (e.key === 'Escape') { e.preventDefault(); keys.current.close(); }
      else if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); keys.current.step(1); }
      else if (e.altKey && e.key === 'ArrowUp') { e.preventDefault(); keys.current.step(-1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openId]);

  /* ── Phones: one column at a time ──────────────────────────────────── */
  const [phoneCol, setPhoneCol] = useState<string | null>(null);
  const shownCol = phone ? (phoneCol && order.includes(phoneCol) ? phoneCol : (columns.find((c) => c.count > 0) || columns[0] || { key: null }).key) : null;
  const visible = phone ? columns.filter((c) => c.key === shownCol) : columns;
  const switcher = useRef<HTMLDivElement>(null);
  useScrollFade(switcher, [phone, columns.length]);
  // The lanes say "more this way" too (2026-10-30): with a record open the board is narrower, and a column cut at its
  // right edge must read as the board scrolling under its own region -- never as the record covering it.
  const lanesRef = useRef<HTMLDivElement>(null);
  useScrollFade(lanesRef, [phone, visible.length, Boolean(openId), columns]);
  // The open card stays in view when the board narrows beside the record (its column scrolls into the lanes).
  useEffect(() => {
    if (!openId || sheet || typeof window === 'undefined') return undefined;
    const raf = typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame : (f: FrameRequestCallback) => window.setTimeout(f, 0);
    const caf = typeof window.cancelAnimationFrame === 'function' ? window.cancelAnimationFrame : window.clearTimeout;
    const h = raf(() => {
      const key = opener.current && opener.current.record === openId ? opener.current.card : openId;
      const btn = openBtns.current[key];
      const col = btn ? btn.closest<HTMLElement>('.okb-col') : null;
      const lanes = lanesRef.current;
      if (!col || !lanes) return;
      const l = col.offsetLeft - lanes.offsetLeft;
      if (l < lanes.scrollLeft || l + col.offsetWidth > lanes.scrollLeft + lanes.clientWidth) {
        const left = Math.max(0, l - 14);
        if (typeof lanes.scrollTo === 'function') lanes.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        else lanes.scrollLeft = left;
      }
    });
    return () => caf(h as number);
  }, [openId, sheet]);

  const moveFor = (card: C) => offeredMoves(transitions, card, ctx, order);
  const onMove = (card: C) => (m: OfferedMove) => act(card, m.inPlace
    ? { kind: 'act', act: m.act, from: card.column, to: card.column, opens: m.opens }
    : resolveDrop(transitions, card, card.column, m.to, ctx));
  const nf = new Intl.NumberFormat('en-US');

  const docked = Boolean(openId && detail && !error);
  /*
   * The record docks beside the WHOLE board (2026-10-30, owner: the panel "overlays the board"): the stage -- toolbar,
   * Elsewhere, status line and lanes -- narrows beside it, so no column is ever under the record. The lanes scroll
   * sideways inside the stage. One scroll context for the record: it grows with its content and its header stays in
   * view (kanban.css), instead of a second scrollbar inside a fixed-height panel.
   */
  return (
    <section className={`board okb${docked ? ' has-dock' : ''}${covered ? ' has-sheet' : ''}`} aria-labelledby={`${idBase}-title`} id={idBase} aria-busy={loading || undefined}>
      <h2 id={`${idBase}-title`} className="okb-sr">{label}</h2>
      <div className={`okb-split${docked ? ' has-dock' : ''}`} ref={bodyRef}>
      <div className="okb-stage">
      {toolbar && <KanbanToolbar {...toolbar} />}
      {elsewhere && <ElsewhereStrip label={elsewhere.label} buckets={elsewhere.buckets} note={elsewhere.note} />}
      <p className={`okb-said${said ? ` okb-said--${said.tone}` : ''}`} role="status" aria-live="polite" tabIndex={-1} ref={saidRef}>
        {said && <span>{said.text}</span>}
        {said && said.instead && (() => {
          const card = byId.get(said.instead.cardId);
          return card ? (
            <button type="button" className="okb-btn okb-btn--quiet" onClick={() => { const ins = said.instead!; setSaid(null); onAct(card, ins.act, { from: card.column, to: null }); }}>
              {said.instead.label}
            </button>
          ) : null;
        })()}
        {said && <button type="button" className="okb-said__x" onClick={() => setSaid(null)} aria-label="Dismiss this message"><span aria-hidden="true">&times;</span></button>}
      </p>
      {error ? (
        <div className="okb-state" role="alert">
          <p>{error}</p>
          {onRetry && <button type="button" className="okb-btn" onClick={onRetry}>Try again</button>}
        </div>
      ) : (
        <div className={`board-body okb-body${docked ? ' has-dock' : ''}${covered ? ' has-sheet' : ''}`} ref={lanesBodyRef}>
          <div className="okb-lanes-wrap">
            {phone && columns.length > 0 && (
              <div className="okb-colswitch" role="group" aria-label="Column shown" ref={switcher}>
                {columns.map((c) => (
                  <button key={c.key} type="button" aria-pressed={c.key === shownCol} onClick={() => setPhoneCol(c.key)}>
                    {c.label}<span className="okb-col__count" aria-hidden="true">{nf.format(c.count)}</span>
                    <span className="okb-sr">, {nf.format(c.count)}</span>
                  </button>
                ))}
              </div>
            )}
            <DndContext sensors={sensors} collisionDetection={collision} onDragStart={onDragStart} onDragOver={onDragOver}
              onDragEnd={onDragEnd} onDragCancel={onDragCancel}
              accessibility={{
                announcements,
                screenReaderInstructions: { draggable: 'To move this card, press Space or Enter. Left and right arrows choose a column; Space or Enter drops; Escape cancels. A drop opens the act for that move and records nothing.' },
              }}>
              <div className={`okb-lanes${dragging ? ' is-dragging' : ''}`} data-columns={visible.length} ref={lanesRef}>
                {loading && columns.length === 0 && <p className="okb-state">Loading the board…</p>}
                {visible.map((col) => {
                  const h = hintFor(col.key);
                  return (
                    <KanbanColumn key={col.key} columnKey={col.key} label={col.label} what={col.what} count={col.count}
                      loaded={col.cards.length} hasMore={Boolean(col.has_more)} loadingMore={col.loading_more}
                      onLoadMore={onLoadMore ? () => onLoadMore(col.key) : undefined}
                      hint={h.hint} hintText={overCol === col.key || phone ? h.text : (h.hint === 'act' ? h.text : null)} emptyText={emptyText}
                      unit={col.unit || unit} history={col.history} note={col.note}>
                      {col.cards.map((card) => (
                        <WorkCard key={card.id} id={card.id} column={card.column} name={cardName(card)}
                          selected={openId !== null && openId === recordOf(card)} onOpen={() => open(card)}
                          moves={moveFor(card)} columnLabel={labelOf} onMove={onMove(card)}
                          dragDisabled={dragDisabled || phone || (transitions.canDrag ? !transitions.canDrag(card, ctx) : false)}
                          openRef={(el) => { openBtns.current[card.id] = el; }}>
                          {renderCard(card)}
                        </WorkCard>
                      ))}
                    </KanbanColumn>
                  );
                })}
              </div>
              {columns.some((c) => c.what) && (
                <details className="okb-about">
                  <summary>How the columns are decided</summary>
                  <dl>
                    {columns.filter((c) => c.what).map((c) => <React.Fragment key={c.key}><dt>{c.label}</dt><dd>{c.what}</dd></React.Fragment>)}
                  </dl>
                </details>
              )}
              <DragOverlay dropAnimation={prefersReducedMotion() ? null : undefined}>
                {dragging ? <div className="okb-card okb-card--overlay" aria-hidden="true"><span className="okb-card__body">{renderCard(dragging)}</span></div> : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      )}
      </div>
        {docked && detail && (
          <DetailDock
            id={`${idBase}-dock`}
            label={detail.label(openCard)}
            title={detail.title(openCard)}
            meta={detail.meta ? detail.meta(openCard) : null}
            index={openIdx >= 0 ? openIdx + 1 : 1}
            count={openCol ? openCol.cards.length : 1}
            onPrev={openIdx > 0 ? () => step(-1) : null}
            onNext={openCol && openIdx >= 0 && openIdx < openCol.cards.length - 1 ? () => step(1) : null}
            onClose={close}
            sheet={sheet}
            sections={[{ id: 'record', label: detail.label(openCard), render: () => detail.render(openCard) }]}
            memoryKey={detail.memoryKey}
            headingRef={headingRef}
            recordKey={openId || ''}
          />
        )}
      </div>
    </section>
  );
}
